import { AiOutlineCrown } from '@react-icons/all-files/ai/AiOutlineCrown'
import { AiOutlineTeam } from '@react-icons/all-files/ai/AiOutlineTeam'
import { PiCertificate } from '@react-icons/all-files/pi/PiCertificate'
import { PiGear } from '@react-icons/all-files/pi/PiGear'
import { PiPresentationChart } from '@react-icons/all-files/pi/PiPresentationChart'
import { PiReceipt } from '@react-icons/all-files/pi/PiReceipt'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CreditPurchaseCard } from '~/billing/components/CreditPurchaseCard'
import type { AllLicensesResponse, LicenseResponse } from '~/billing/services/billingService'
import {
  addAccountMember,
  cancelAccountInvitation,
  cancelSubscription as cancelSubscriptionService,
  createCustomerPortalSession,
  getCreditBalance,
  getPaymentDetails,
  inviteAccountMembersByEmail,
  removeAccountMember,
  revertCancellation as revertCancellationService
} from '~/billing/services/billingService'
import { Button } from '~/components/common/Button'
import { CustomTooltip } from '~/components/common/CustomTooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { EmailTagInput, EmailTagInputRef } from '~/components/common/EmailTagInput'
import { InlineDropdown } from '~/components/common/dropdowns/InlineDropdown'
import { InputArea } from '~/components/common/InputArea'
import { List } from '~/components/common/List'
import { Pagination } from '~/components/common/Pagination'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/common/Popover'
import { ProgressBarLine } from '~/components/common/progress-bar/ProgressBarLine'
import { Tag } from '~/components/common/Tag'
import { Typography } from '~/components/common/Typography'
import { ManageRightArrowIcon, PlanCheckIcon, UserIcon } from '~/components/icons'
import { Modal } from '~/components/modals/CurieModal'
import { DashboardContent } from '~/components/usage-dashboard'
import { DialogHeader } from '~/components/usage-dashboard/DialogHeader'
import { ChevronDownIcon } from '~/components/usage-dashboard/icons'
import { getIsBillableFlowPlan } from '~/components/usage-dashboard/types'
import { useUserSpecificConfig } from '~/contexts/AppConfigContext'
import { useBillingContext } from '~/contexts/BillingContext'
import { useModalContext } from '~/contexts/ModalContext'
import { useUserContext } from '~/contexts/UserContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { bridge } from '~/services/bridge'
import { removeOrgUser, updateUserRole } from '~/services/organizationService'
import type { BillableFlowDetailsParams } from '~/services/usageDashboardService'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { isUnlimited } from '~/utils/quotaConstants'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { formatDisplayDate } from '~/utils/timezone'
import { BetterToast } from '~/utils/toast'
import {
  type BillingHistoryItem,
  buildBillingHistory,
  formatBillableFlowsLimit,
  formatCreditLimit,
  formatLicenseDate,
  getLicensePeriodEndLabel,
  isBillableFlowLicense
} from './manageAccountHelpers'
import {
  ManageAccountReadOnlyProvider,
  useManageAccountReadOnly
} from './ManageAccountReadOnlyContext'
import { PlanComponent } from './PlanDialog'

const BillableFlowsPanel = React.lazy(() =>
  import('~/components/usage-dashboard/BillableFlowsTable').then(mod => ({
    default: mod.BillableFlowsPanel
  }))
)

// Dashboard chart icon with bar graph
const DashboardTabIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='21'
    viewBox='0 0 13 14'
    fill='none'
    className={className}
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M12 1.5H7V0.5C7 0.223858 6.77614 0 6.5 0C6.22386 0 6 0.223858 6 0.5V1.5H1C0.447715 1.5 0 1.94772 0 2.5V10C0 10.5523 0.447715 11 1 11H3.46L2.10938 12.6875C1.93679 12.9032 1.97176 13.218 2.1875 13.3906C2.40324 13.5632 2.71804 13.5282 2.89063 13.3125L4.74 11H8.26L10.1094 13.3125C10.282 13.5282 10.5968 13.5632 10.8125 13.3906C11.0282 13.218 11.0632 12.9032 10.8906 12.6875L9.54 11H12C12.5523 11 13 10.5523 13 10V2.5C13 1.94772 12.5523 1.5 12 1.5ZM12 10H1V2.5H12V10ZM5 6.5V8C5 8.27614 4.77614 8.5 4.5 8.5C4.22386 8.5 4 8.27614 4 8V6.5C4 6.22386 4.22386 6 4.5 6C4.77614 6 5 6.22386 5 6.5ZM7 5.5V8C7 8.27614 6.77614 8.5 6.5 8.5C6.22386 8.5 6 8.27614 6 8V5.5C6 5.22386 6.22386 5 6.5 5C6.77614 5 7 5.22386 7 5.5ZM9 4.5V8C9 8.27614 8.77614 8.5 8.5 8.5C8.22386 8.5 8 8.27614 8 8V4.5C8 4.22386 8.22386 4 8.5 4C8.77614 4 9 4.22386 9 4.5Z'
      fill='currentColor'
    />
  </svg>
)

type Tab = 'usage' | 'users' | 'account' | 'license' | 'billing' | 'upgrade'

interface UsageComponentProps {
  licenseData: AllLicensesResponse
  handleTabChange: (tab: Tab) => void
  onClose: () => void
  canShowUpgrade?: boolean
}

interface UsageCardProps {
  children: React.ReactNode
}

const COMPACT_LAYOUT_MAX_WIDTH = 960

function useCompactView() {
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth <= COMPACT_LAYOUT_MAX_WIDTH
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia(`(max-width: ${COMPACT_LAYOUT_MAX_WIDTH}px)`)

    const handleChange = (event: MediaQueryListEvent) => {
      setIsNarrowViewport(event.matches)
    }

    setIsNarrowViewport(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  return bridge.isInPlugin() || isNarrowViewport
}

interface TeamMember {
  id: number | null
  name: string | null
  email: string
  role: 'admin' | 'member'
  billingAdmin?: boolean
  workspaces?: Array<{
    org_id: number
    org_name: string
    role: string
    added_time: string
  }>
  status: 'active' | 'pending'
  invitationId?: number | null
  expiresAt?: string | null
}

function UsageCard({ children }: UsageCardProps) {
  const isCompact = useCompactView()
  return (
    <div
      className={`flex flex-col items-start gap-2.5 rounded-3xl border border-[#EAEAEA] bg-[#FCFCFC] h-full ${isCompact ? 'py-4 px-4' : 'py-6 px-[32px]'}`}
    >
      {children}
    </div>
  )
}

function TruncateCell({
  children,
  tooltipContent
}: {
  children: React.ReactNode
  tooltipContent: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const checkTruncation = useCallback(() => {
    const el = ref.current
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth)
    }
  }, [])

  return (
    <CustomTooltip content={tooltipContent} placement='top' isDisabled={!isTruncated}>
      <div ref={ref} className='truncate whitespace-nowrap' onMouseEnter={checkTruncation}>
        {children}
      </div>
    </CustomTooltip>
  )
}

function UsageComponent({
  licenseData,
  handleTabChange,
  onClose,
  canShowUpgrade = true
}: UsageComponentProps) {
  const readOnly = useManageAccountReadOnly()
  const { openPlan } = useModalContext()
  const isCompact = useCompactView()
  const { billingInfo } = useBillingContext()
  const { userConfig } = useUserSpecificConfig()
  const isSuperAdmin = userConfig?.is_super_admin ?? false
  const [showFlowsPanel, setShowFlowsPanel] = useState(false)

  const flowDetailsParams: BillableFlowDetailsParams = useMemo(
    () => ({ timeRange: 'billing_cycle', personalOnly: false }),
    []
  )

  // Use license-based checks
  // Check for unlimited credits using centralized utility
  const hasUnlimitedCredits = isUnlimited(licenseData.credits_limit)
  const hasNoLicense = licenseData.total_licenses === 0
  const hasEnterpriseLicense = licenseData.licenses.some(license =>
    license.license_name?.toLowerCase().includes('enterprise')
  )
  const hasTeamLicense = licenseData.licenses.some(license =>
    license.license_name?.toLowerCase().includes('team')
  )
  const isBillableFlowPlan = getIsBillableFlowPlan(null, licenseData)

  const handleUpgrade = () => {
    if (readOnly) return
    if (hasNoLicense) {
      onClose()
      openPlan()
    } else {
      handleTabChange('upgrade')
    }
  }

  return (
    <div className={`flex flex-col items-center ${isCompact ? 'gap-4' : 'gap-6'}`}>
      <div className={`self-stretch ${isCompact ? 'flex flex-col gap-4' : 'flex gap-6'}`}>
        <div
          className={`flex-1 min-w-0 relative ${readOnly ? '' : 'cursor-pointer'}`}
          onClick={readOnly ? undefined : () => setShowFlowsPanel(true)}
          role={readOnly ? undefined : 'button'}
          aria-label={readOnly ? undefined : 'View flow details'}
          tabIndex={readOnly ? undefined : 0}
          onKeyDown={
            readOnly
              ? undefined
              : e => {
                  if (e.key === 'Enter') setShowFlowsPanel(true)
                }
          }
        >
          <UsageCard>
            <div className='w-full flex flex-col gap-2'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='medium'
                className='self-stretch text-[#2E1F36]'
              >
                {isBillableFlowPlan
                  ? 'Billable flows this period'
                  : hasNoLicense
                    ? 'Credit usage this month'
                    : 'Credit usage this period'}
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='titleLg'
                fontWeight='bold'
                className='text-[#65558F]'
              >
                {isBillableFlowPlan ? (
                  // For billable flow plans, show flows used / limit
                  <>
                    {licenseData.billable_flows_used ?? 0}
                    {licenseData.billable_flows_limit != null
                      ? `/${licenseData.billable_flows_limit}`
                      : ''}
                  </>
                ) : (
                  // For credit-based plans, show credits used / limit
                  <>
                    {licenseData.credits_used}
                    {hasUnlimitedCredits ? '' : `/${licenseData.credits_limit}`}
                  </>
                )}
              </Typography>
              {/* Show progress bar and period info for credit-based plans */}
              {!hasUnlimitedCredits && !isBillableFlowPlan && (
                <>
                  <ProgressBarLine
                    color='#332838'
                    used={licenseData.credits_used}
                    total={licenseData.credits_limit}
                  />
                  {/* Only show period dates for accounts with subscriptions */}
                  {!hasNoLicense && licenseData.period_start && (
                    <div className='mt-[10px] flex flex-col gap-0.5'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='text-tokens-neutral-text-tertiary'
                      >
                        Start Date: {formatDisplayDate(licenseData.period_start)}
                      </Typography>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='text-tokens-neutral-text-tertiary'
                      >
                        End Date:{' '}
                        {licenseData.period_end
                          ? formatDisplayDate(licenseData.period_end)
                          : 'Present'}
                      </Typography>
                    </div>
                  )}
                </>
              )}
              {/* Show progress bar for billable flow plans */}
              {isBillableFlowPlan && licenseData.billable_flows_limit != null && (
                <>
                  <ProgressBarLine
                    color='#332838'
                    used={licenseData.billable_flows_used ?? 0}
                    total={licenseData.billable_flows_limit}
                  />
                  {/* Show period dates for billable flow plans */}
                  {!hasNoLicense && licenseData.period_start && (
                    <div className='mt-[10px] flex flex-col gap-0.5'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='text-tokens-neutral-text-tertiary'
                      >
                        Start Date: {formatDisplayDate(licenseData.period_start)}
                      </Typography>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='text-tokens-neutral-text-tertiary'
                      >
                        End Date:{' '}
                        {licenseData.period_end
                          ? formatDisplayDate(licenseData.period_end)
                          : 'Present'}
                      </Typography>
                    </div>
                  )}
                  {/* Show info about credits being N/A for billable flow plans */}
                  <div className='mt-2 p-3 bg-blue-50 border border-blue-200 rounded-xl'>
                    <Typography fontFamily='geist' fontSize='bodySm' className='text-blue-700'>
                      Credits: N/A (This plan is billed per migration task)
                    </Typography>
                  </div>
                </>
              )}
              {/* Show Manage Overage button for subscription plans */}
              {!hasNoLicense && !licenseData.is_free_license && (
                <Button
                  variant='borderless'
                  onClick={() => handleTabChange(hasEnterpriseLicense ? 'license' : 'billing')}
                  isDisabled={readOnly}
                  className='font-medium self-start cursor-pointer hover:bg-transparent hover:opacity-100 transition-colors text-[12px] text-black p-0 gap-2 h-auto leading-4'
                >
                  Manage overage
                  <ManageRightArrowIcon />
                </Button>
              )}
              {licenseData.credits_used >= licenseData.credits_limit &&
                !hasUnlimitedCredits &&
                !isBillableFlowPlan &&
                canShowUpgrade &&
                !readOnly && (
                  <Button
                    variant='primary'
                    onClick={handleUpgrade}
                    className='text-white hover:bg-gray-700 transition-colors flex flex-col justify-center items-center gap-2 self-stretch rounded-xl bg-[#262626] text-sm w-[90px] h-9'
                  >
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyMd'
                      fontWeight='normal'
                      className='text-inherit'
                    >
                      Upgrade
                    </Typography>
                  </Button>
                )}
            </div>
          </UsageCard>
          <svg
            className='absolute top-4 right-4 w-5 h-5 text-[rgba(0,0,0,0.35)]'
            viewBox='0 0 20 20'
            fill='currentColor'
          >
            <path
              fillRule='evenodd'
              d='M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z'
              clipRule='evenodd'
            />
          </svg>
        </div>
        {/* Show team members card if there's a license with user limit */}
        {(hasTeamLicense || hasEnterpriseLicense) && (
          <div className='flex-1 min-w-0'>
            <UsageCard>
              <div className='w-full flex flex-col gap-2'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-[#2E1F36]'
                >
                  Team members
                </Typography>
                <Typography
                  fontFamily='geist'
                  fontSize='titleLg'
                  fontWeight='bold'
                  className='text-[#65558F]'
                >
                  {licenseData.user_count}
                  {isUnlimited(licenseData.user_limit) ? '' : `/${licenseData.user_limit}`}
                </Typography>
                {!isUnlimited(licenseData.user_limit) && licenseData.user_limit > 0 && (
                  <ProgressBarLine
                    color='#332838'
                    used={licenseData.user_count}
                    total={licenseData.user_limit}
                  />
                )}
                <Button
                  variant='borderless'
                  onClick={() => handleTabChange('users')}
                  isDisabled={readOnly}
                  className='font-medium self-start cursor-pointer hover:bg-transparent hover:opacity-100 transition-colors text-[12px] text-black p-0 gap-2 h-auto leading-4'
                >
                  Manage
                  <ManageRightArrowIcon />
                </Button>
              </div>
            </UsageCard>
          </div>
        )}
        {/* Billable Flows usage card - always shown */}
        <div className='flex-1 min-w-0'>
          <UsageCard>
            <div className='w-full flex flex-col gap-2'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='medium'
                className='text-[#2E1F36]'
              >
                Billable Flows
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='titleLg'
                fontWeight='bold'
                className='text-[#65558F]'
              >
                {(licenseData.billable_flows_used ?? 0).toLocaleString()}
                {'/'}
                {licenseData.billable_flows_limit != null && licenseData.billable_flows_limit > 0
                  ? licenseData.billable_flows_limit.toLocaleString()
                  : 'N/A'}
              </Typography>
            </div>
          </UsageCard>
        </div>
      </div>
      {billingInfo?.id && showFlowsPanel && !readOnly && (
        <React.Suspense fallback={null}>
          <BillableFlowsPanel
            isOpen={showFlowsPanel}
            onClose={() => setShowFlowsPanel(false)}
            billingAccountId={billingInfo.id}
            params={flowDetailsParams}
            hideCredits={isBillableFlowPlan}
            isSuperAdmin={isSuperAdmin}
          />
        </React.Suspense>
      )}
      {canShowUpgrade && !readOnly && (
        <div className='text-center'>
          <Button
            variant='borderless'
            onClick={handleUpgrade}
            className='text-[#846FE1] text-center cursor-pointer hover:bg-transparent hover:opacity-100'
          >
            Upgrade Plan
          </Button>
        </div>
      )}
    </div>
  )
}

interface ManageDropdownProps {
  member: TeamMember
  onAssignRole: (userId: number, role: 'admin' | 'member') => void
  onRemoveUser: (userId: number) => void
  onCancelInvite: (invitationId: number) => void
}

function ManageDropdown({
  member,
  onAssignRole,
  onRemoveUser,
  onCancelInvite
}: ManageDropdownProps) {
  const readOnly = useManageAccountReadOnly()
  const isAdmin = member.billingAdmin
  const isPending = member.status === 'pending'

  if (readOnly) {
    return (
      <Typography
        fontFamily='geist'
        fontSize='bodySm'
        className='text-tokens-neutral-text-tertiary'
      >
        —
      </Typography>
    )
  }

  // For pending invitations, show cancel invite option
  if (isPending && member.invitationId) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='borderless'
            className='w-9 h-9 p-0 hover:bg-gray-100 rounded-xl transition-colors flex items-center justify-center'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <circle cx='4' cy='8' r='1.5' fill='rgba(0,0,0,0.45)' />
              <circle cx='8' cy='8' r='1.5' fill='rgba(0,0,0,0.45)' />
              <circle cx='12' cy='8' r='1.5' fill='rgba(0,0,0,0.45)' />
            </svg>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-[200px]'>
          <DropdownMenuItem
            className='text-[14px] font-medium text-[#C22C15] rounded-lg px-3 py-2'
            onClick={() => onCancelInvite(member.invitationId!)}
          >
            Cancel Invite
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  // Can't manage users without a valid id
  if (member.id === null) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='borderless'
          className='w-9 h-9 p-0 hover:bg-gray-100 rounded-xl transition-colors flex items-center justify-center'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
          >
            <circle cx='4' cy='8' r='1.5' fill='rgba(0,0,0,0.45)' />
            <circle cx='8' cy='8' r='1.5' fill='rgba(0,0,0,0.45)' />
            <circle cx='12' cy='8' r='1.5' fill='rgba(0,0,0,0.45)' />
          </svg>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-[200px]'>
        {isAdmin ? (
          // Admin user: show option to demote to Member
          <DropdownMenuItem
            className='text-[14px] font-medium text-[rgba(0,0,0,0.88)] rounded-lg px-3 py-2'
            onClick={() => onAssignRole(member.id!, 'member')}
          >
            Set as Member
          </DropdownMenuItem>
        ) : (
          // Member user: show option to promote to Account Admin
          <DropdownMenuItem
            className='text-[14px] font-medium text-[rgba(0,0,0,0.88)] rounded-lg px-3 py-2'
            onClick={() => onAssignRole(member.id!, 'admin')}
          >
            Set as Account Admin
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator className='my-1' />

        <DropdownMenuItem
          className='text-[14px] font-medium text-[#C22C15] rounded-lg px-3 py-2'
          onClick={() => onRemoveUser(member.id!)}
        >
          Remove User
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type ViewMode = 'account' | number // 'account' for Account View, or workspace org_id

function UsersComponent() {
  const isCompact = useCompactView()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [teamMembers, setTeamMembers] = useState<TeamMember[] | null>(null)
  const [allWorkspaces, setAllWorkspaces] = useState<
    Array<{ org_id: number; org_name: string; role: string; is_default?: boolean }>
  >([])
  const [currentPage, setCurrentPage] = useState(1)
  const [viewMode, setViewMode] = useState<ViewMode>('account')
  const itemsPerPage = 8
  const readOnly = useManageAccountReadOnly()
  const { workspaces } = useWorkspaceContext()
  const { billingInfo } = useBillingContext()
  const { user: currentUser } = useUserContext()

  // Email invite state
  const [inviteEmails, setInviteEmails] = useState<string[]>([])
  const [hasPendingInviteEmail, setHasPendingInviteEmail] = useState(false)
  const [inviteMode, setInviteMode] = useState<'admin' | 'all' | 'workspaces'>('admin')
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<number[]>([])
  const [isInviting, setIsInviting] = useState(false)
  const [inviteSuccess, setInviteSuccess] = useState(false)
  const [workspaceSelectorOpen, setWorkspaceSelectorOpen] = useState(false)
  const inviteInputRef = useRef<EmailTagInputRef>(null)

  const handleInviteSubmit = async () => {
    if (readOnly || !billingInfo?.id || inviteSuccess) return

    const pendingInviteEmail = inviteInputRef.current?.getPendingValidEmail() ?? null
    const emailsToInvite =
      pendingInviteEmail && !inviteEmails.includes(pendingInviteEmail)
        ? [...inviteEmails, pendingInviteEmail]
        : inviteEmails

    if (emailsToInvite.length === 0) return

    if (pendingInviteEmail) {
      inviteInputRef.current?.commitPendingEmail()
    }

    setIsInviting(true)
    try {
      const inviteTypeMap = {
        admin: 'account_admin',
        all: 'all_workspaces',
        workspaces: 'specific_workspaces'
      } as const

      const result = await inviteAccountMembersByEmail(billingInfo.id, {
        emails: emailsToInvite,
        invite_type: inviteTypeMap[inviteMode],
        workspace_ids: inviteMode === 'workspaces' ? selectedWorkspaces : undefined
      })
      if (result.invited.length > 0) {
        BetterToast.success(`Invitation sent to ${result.invited.length} user(s)`)
        setInviteEmails([])
        setHasPendingInviteEmail(false)
        setInviteSuccess(true)
        fetchTeamMembers()
      }
      if (result.failed.length > 0) {
        BetterToast.error(`Failed to invite: ${result.failed.join(', ')}`)
      }
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to send invitations')
    } finally {
      setIsInviting(false)
    }
  }

  // Role assignment confirmation modal state
  const [roleConfirmModal, setRoleConfirmModal] = useState<{
    isOpen: boolean
    userId: number | null
    role: 'admin' | 'member' | null
  }>({ isOpen: false, userId: null, role: null })

  // Remove user confirmation modal state
  const [removeConfirmModal, setRemoveConfirmModal] = useState<{
    isOpen: boolean
    member: TeamMember | null
  }>({ isOpen: false, member: null })

  // Fetch team members function (extracted for reuse)
  const fetchTeamMembers = useCallback(() => {
    if (!billingInfo?.id) return

    // Fetch all users in billing account (not filtered by workspace)
    // This ensures we get all users regardless of which workspace they belong to
    fetchWrapper(`/api/billing/${billingInfo.id}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => response.json())
      .then(data => {
        const teamMembersData = data.map(
          (user: {
            user_id: number | null
            name: string | null
            email: string
            is_billing_admin: boolean
            workspaces: Array<{
              org_id: number
              org_name: string
              role: string
              added_time: string
              is_default?: boolean
            }>
            status: 'active' | 'pending'
            invitation_id?: number | null
            expires_at?: string | null
          }) => ({
            id: user.user_id,
            name: user.name,
            email: user.email,
            role: user.is_billing_admin ? ('admin' as const) : ('member' as const),
            billingAdmin: user.is_billing_admin,
            workspaces: user.workspaces,
            status: user.status,
            invitationId: user.invitation_id,
            expiresAt: user.expires_at
          })
        )
        setTeamMembers(teamMembersData)

        // Extract unique workspaces from all members
        const wsMap = new Map<
          number,
          { org_id: number; org_name: string; role: string; is_default?: boolean }
        >()
        teamMembersData.forEach((member: TeamMember) => {
          member.workspaces?.forEach(ws => {
            if (!wsMap.has(ws.org_id)) {
              wsMap.set(ws.org_id, ws)
            }
          })
        })
        setAllWorkspaces(Array.from(wsMap.values()))
      })
      .catch(err => {
        console.warn('Failed to fetch team members:', err)
      })
  }, [billingInfo?.id])

  // Fetch team members on component mount
  useEffect(() => {
    fetchTeamMembers()
  }, [fetchTeamMembers])

  // Listen for role changes from other components (e.g., WorkspaceSettingsTab)
  useEffect(() => {
    const handleRolesUpdated = () => {
      fetchTeamMembers()
    }

    globalEventEmitter.on(AppEvents.MemberRolesUpdated, handleRolesUpdated)

    return () => {
      globalEventEmitter.off(AppEvents.MemberRolesUpdated, handleRolesUpdated)
    }
  }, [fetchTeamMembers])

  // Open confirmation modal for role assignment
  const handleAssignRole = (userId: number, role: 'admin' | 'member') => {
    if (readOnly) return
    setRoleConfirmModal({ isOpen: true, userId, role })
  }

  // Close role confirmation modal
  const closeRoleConfirmModal = () => {
    setRoleConfirmModal({ isOpen: false, userId: null, role: null })
  }

  // Confirm and execute role assignment
  const confirmAssignRole = async () => {
    const { userId, role } = roleConfirmModal
    if (readOnly || !userId || !role || !billingInfo?.id) {
      closeRoleConfirmModal()
      return
    }

    try {
      if (role === 'admin') {
        // Assign as billing account admin
        await addAccountMember(billingInfo.id, userId, 'admin')
        setTeamMembers(
          prev =>
            prev?.map(m => (m.id === userId ? { ...m, billingAdmin: true, role: 'admin' } : m)) ??
            null
        )
        globalEventEmitter.emit(AppEvents.MemberRolesUpdated)
        BetterToast.success('User assigned as Account Admin')
      } else {
        // Demote from billing account admin to regular member
        const member = teamMembers?.find(m => m.id === userId)

        // Remove from AccountMemberships (this removes billing admin status)
        if (member?.billingAdmin) {
          await removeAccountMember(billingInfo.id, userId)
        }

        // Also demote from workspace admin if viewing a specific workspace
        if (typeof viewMode === 'number') {
          const targetWorkspace = workspaces.find(ws => ws.id === viewMode)
          const memberWorkspace = member?.workspaces?.find(ws => ws.org_id === viewMode)
          if (targetWorkspace && memberWorkspace?.role === 'admin') {
            await updateUserRole(targetWorkspace.uuid, userId, 'user')
          }
        }

        fetchTeamMembers()
        globalEventEmitter.emit(AppEvents.MemberRolesUpdated)
        BetterToast.success('User demoted to Member')
      }
    } catch (error) {
      console.error('Failed to assign role:', error)
      BetterToast.error(error instanceof Error ? error.message : 'Failed to assign role')
    } finally {
      closeRoleConfirmModal()
    }
  }

  // Open remove confirmation modal
  const handleRemoveUser = (userId: number) => {
    if (readOnly) return
    const member = teamMembers?.find(m => m.id === userId)
    if (member) {
      setRemoveConfirmModal({ isOpen: true, member })
    }
  }

  // Close remove confirmation modal
  const closeRemoveConfirmModal = () => {
    setRemoveConfirmModal({ isOpen: false, member: null })
  }

  // Confirm and execute user removal
  const confirmRemoveUser = async () => {
    const { member } = removeConfirmModal
    if (readOnly || !member || member.id === null || !billingInfo?.id) {
      closeRemoveConfirmModal()
      return
    }

    try {
      const isBillingAdmin = member.billingAdmin
      const memberId = member.id

      if (typeof viewMode === 'number') {
        // Viewing a specific workspace - find its UUID and remove from that workspace
        const targetWorkspace = workspaces.find(ws => ws.id === viewMode)
        if (!targetWorkspace) {
          BetterToast.error('Workspace not found')
          closeRemoveConfirmModal()
          return
        }

        await removeOrgUser(targetWorkspace.uuid, memberId)

        // If also a billing admin, remove from account
        if (isBillingAdmin) {
          await removeAccountMember(billingInfo.id, memberId)
        }

        // Refresh team members from server to get accurate data
        fetchTeamMembers()
      } else {
        // Account view - only billing admins can be removed from here
        if (!isBillingAdmin) {
          BetterToast.error('Please select a specific workspace to remove this user')
          closeRemoveConfirmModal()
          return
        }

        await removeAccountMember(billingInfo.id, memberId)
        // Refresh team members from server to get accurate data
        fetchTeamMembers()
      }
      BetterToast.success('User removed successfully')
    } catch (error) {
      console.error('Failed to remove user:', error)
      BetterToast.error(error instanceof Error ? error.message : 'Failed to remove user')
    } finally {
      closeRemoveConfirmModal()
    }
  }

  // Handle cancel invitation
  const handleCancelInvite = async (invitationId: number) => {
    if (readOnly || !billingInfo?.id) return

    try {
      await cancelAccountInvitation(billingInfo.id, invitationId)
      // Remove the invitation from local state
      setTeamMembers(prev => prev?.filter(m => m.invitationId !== invitationId) ?? null)
      BetterToast.success('Invitation cancelled')
    } catch (error) {
      console.error('Failed to cancel invitation:', error)
      BetterToast.error(error instanceof Error ? error.message : 'Failed to cancel invitation')
    }
  }

  if (!teamMembers)
    return (
      <div className='flex items-center justify-center h-32'>
        <Typography fontFamily='geist' fontSize='bodyMd' className='text-gray-500 ml-3'>
          Loading team members...
        </Typography>
      </div>
    )

  // Filter members based on view mode
  // Billing admins should appear in all workspace views (they have access to all workspaces)
  const filteredMembers =
    viewMode === 'account'
      ? teamMembers
      : teamMembers.filter(
          member => member.billingAdmin || member.workspaces?.some(ws => ws.org_id === viewMode)
        )

  // Filter out personal workspaces (can't invite to or view personal workspaces)
  const nonPersonalWorkspaces = allWorkspaces.filter(ws => !ws.is_default)

  // Pagination logic
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedMembers = filteredMembers.slice(startIndex, startIndex + itemsPerPage)

  // Get current view name for display
  const getViewLabel = (mode: ViewMode) => {
    if (mode === 'account') return 'Account View'
    const workspace = allWorkspaces.find(ws => ws.org_id === mode)
    return workspace?.org_name || 'Workspace View'
  }

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode)
    setCurrentPage(1)
  }

  const getRoleBadge = (member: TeamMember) => {
    if (member.billingAdmin) {
      return <Tag variant='info'>Account Admin</Tag>
    }

    // When viewing a specific workspace, show that workspace's role
    if (typeof viewMode === 'number') {
      const viewedWorkspace = (member.workspaces || []).find(ws => ws.org_id === viewMode)
      if (viewedWorkspace?.role === 'admin') {
        return <Tag variant='warning'>Workspace Admin</Tag>
      }
    }

    return <Tag variant='success'>Member</Tag>
  }

  const renderRoleCell = (member: TeamMember) => {
    // Just display the role badge - view only, no interaction
    return <div className='flex justify-start select-none'>{getRoleBadge(member)}</div>
  }

  const getWorkspaceDisplay = (member: TeamMember) => {
    // Account Admins should show "All"
    if (member.billingAdmin) return 'All'
    const workspaces = member.workspaces || []
    if (workspaces.length === 0) return '-'
    if (workspaces.length === 1) return workspaces[0].org_name
    return `${workspaces[0].org_name}, +${workspaces.length - 1}`
  }

  return (
    <div className='flex flex-col self-stretch w-full h-full' aria-label='Team members section'>
      {/* Header bar */}
      <div
        className={`w-full mb-6 bg-white/50 border border-white rounded-[16px] ${
          isCompact
            ? 'flex flex-col gap-3 px-4 py-4 items-start'
            : 'flex flex-row gap-3 px-6 py-4 items-center justify-between'
        }`}
      >
        <div className='flex flex-row gap-3 items-center h-5'>
          <div className='w-5 h-5 flex items-center justify-center rounded-[96px] bg-[rgba(132,111,225,0.1)]'>
            <UserIcon className='w-[14px] h-[14px]' />
          </div>
          <Typography
            fontFamily='geistMono'
            fontSize='labelSm'
            fontWeight='semibold'
            className='text-tokens-neutral-text-secondary select-none'
          >
            {filteredMembers.length} members in this{' '}
            {viewMode === 'account' ? 'account' : 'workspace'}
          </Typography>
        </div>
        <div className={`flex gap-3 items-center ${isCompact ? 'w-full flex-wrap' : 'flex-row'}`}>
          {/* View selector dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='outlined'
                className={`h-9 text-[rgba(0,0,0,0.88)] hover:bg-gray-50 transition-colors bg-white border border-[#D6D6D6] rounded-xl text-sm flex items-center gap-2 ${
                  isCompact ? 'w-full min-w-0 justify-between' : 'min-w-[140px]'
                }`}
              >
                {getViewLabel(viewMode)}
                <ChevronDownIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-[220px]'>
              <DropdownMenuItem
                onClick={() => handleViewChange('account')}
                className={viewMode === 'account' ? 'text-tokens-primary-2' : ''}
              >
                Account View
                {viewMode === 'account' && <PlanCheckIcon />}
              </DropdownMenuItem>
              {nonPersonalWorkspaces.length > 0 && (
                <>
                  <DropdownMenuSeparator className='my-1' />
                  <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
                  <div className='max-h-[240px] overflow-y-auto'>
                    {nonPersonalWorkspaces.map(ws => (
                      <DropdownMenuItem
                        key={ws.org_id}
                        onClick={() => handleViewChange(ws.org_id)}
                        className={viewMode === ws.org_id ? 'text-tokens-primary-2' : ''}
                      >
                        {ws.org_name}
                        {viewMode === ws.org_id && <PlanCheckIcon />}
                      </DropdownMenuItem>
                    ))}
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {!readOnly && (
            <Popover
              isOpen={inviteOpen}
              onOpenChange={open => {
                if (open) {
                  // Reset state when opening
                  setInviteEmails([])
                  setHasPendingInviteEmail(false)
                  setInviteMode('all')
                  setSelectedWorkspaces([])
                  setInviteSuccess(false)
                  setWorkspaceSelectorOpen(false)
                }
                setInviteOpen(open)
              }}
              placement='bottom-end'
            >
              <PopoverTrigger>
                <Button
                  variant='outlined'
                  className={`h-9 text-[rgba(0,0,0,0.88)] hover:bg-gray-50 transition-colors bg-white border border-[#D6D6D6] rounded-xl text-sm font-medium ${
                    isCompact ? 'w-full px-4 justify-center' : 'px-4'
                  }`}
                >
                  Invite User
                </Button>
              </PopoverTrigger>
              <PopoverContent width={500} className='flex flex-col gap-4'>
                {/* Email Tags Input Row */}
                <div className='flex items-center gap-2'>
                  <EmailTagInput
                    ref={inviteInputRef}
                    emails={inviteEmails}
                    onEmailsChange={emails => {
                      setInviteEmails(emails)
                      setHasPendingInviteEmail(false)
                      setInviteSuccess(false)
                    }}
                    onInputStateChange={({ hasValidPendingEmail }) => {
                      setHasPendingInviteEmail(hasValidPendingEmail)
                    }}
                    onSubmit={handleInviteSubmit}
                    placeholder='Enter user email'
                  />
                  <Button
                    variant='primary'
                    className='h-[44px] px-5'
                    onClick={handleInviteSubmit}
                    isDisabled={
                      (inviteEmails.length === 0 && !hasPendingInviteEmail) ||
                      isInviting ||
                      inviteSuccess
                    }
                  >
                    {isInviting ? 'Sending...' : inviteSuccess ? 'Invited' : 'Invite'}
                  </Button>
                </div>

                {/* Workspace Selector */}
                <div className='flex w-full items-center justify-between'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='medium'
                    className='text-tokens-neutral-text-primary'
                  >
                    Invite User(s) To
                  </Typography>
                  <InlineDropdown
                    isOpen={workspaceSelectorOpen}
                    onOpenChange={setWorkspaceSelectorOpen}
                    width={420}
                    trigger={
                      <span className='max-w-[200px] truncate text-right'>
                        {inviteMode === 'admin'
                          ? 'Account Admin'
                          : inviteMode === 'all'
                            ? 'All Workspaces'
                            : selectedWorkspaces.length === 0
                              ? 'Select Workspaces'
                              : selectedWorkspaces
                                  .map(
                                    id =>
                                      nonPersonalWorkspaces.find(ws => ws.org_id === id)
                                        ?.org_name || ''
                                  )
                                  .filter(Boolean)
                                  .join(', ')}
                      </span>
                    }
                    options={[
                      {
                        id: 'admin',
                        label: 'As Account Admin',
                        isSelected: inviteMode === 'admin'
                      },
                      { id: 'sep1', label: '', isSeparator: true },
                      {
                        id: 'all',
                        label: 'All Workspaces',
                        isSelected: inviteMode === 'all'
                      },
                      ...(nonPersonalWorkspaces.length > 0
                        ? [
                            { id: 'sep2', label: '', isSeparator: true },
                            { id: 'ws-label', label: 'to Workspaces', isLabel: true },
                            ...nonPersonalWorkspaces.map(ws => ({
                              id: ws.org_id,
                              label: ws.org_name,
                              isSelected:
                                inviteMode === 'workspaces' &&
                                selectedWorkspaces.includes(ws.org_id)
                            }))
                          ]
                        : [])
                    ]}
                    multiSelect
                    onSelect={option => {
                      setInviteSuccess(false)
                      if (option.id === 'admin') {
                        setInviteMode('admin')
                        setSelectedWorkspaces([])
                        setWorkspaceSelectorOpen(false)
                      } else if (option.id === 'all') {
                        setInviteMode('all')
                        setSelectedWorkspaces([])
                        setWorkspaceSelectorOpen(false)
                      } else if (typeof option.id === 'number') {
                        // Workspace selection
                        setInviteMode('workspaces')
                        if (selectedWorkspaces.includes(option.id)) {
                          const newSelection = selectedWorkspaces.filter(id => id !== option.id)
                          setSelectedWorkspaces(newSelection)
                          if (newSelection.length === 0) {
                            setInviteMode('all')
                          }
                        } else {
                          setSelectedWorkspaces([...selectedWorkspaces, option.id])
                        }
                      }
                    }}
                    footer={
                      <>
                        <Button variant='outlined' onClick={() => setWorkspaceSelectorOpen(false)}>
                          Cancel
                        </Button>
                        <Button variant='primary' onClick={() => setWorkspaceSelectorOpen(false)}>
                          Select
                        </Button>
                      </>
                    }
                  />
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Table */}
      <div
        className={`flex flex-col flex-1 min-h-0 w-full rounded-[16px] border border-white bg-white/50 overflow-hidden ${
          isCompact ? 'py-3 px-3' : 'py-4 px-6'
        }`}
      >
        <div className='flex-1'>
          {isCompact ? (
            <div className='w-full flex flex-col gap-3'>
              {paginatedMembers.map(member => {
                const { id, name, email, status } = member
                return (
                  <div
                    key={`member-card-${id ?? email}`}
                    className='w-full rounded-xl border border-white bg-white/60 p-3'
                  >
                    <div className='flex items-start justify-between gap-2'>
                      <div className='min-w-0 flex-1'>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='semibold'
                          className={
                            status === 'pending'
                              ? 'text-gray-400 italic select-none'
                              : 'text-gray-900 select-text'
                          }
                        >
                          {name ?? 'Pending invitation'}
                        </Typography>
                        <Typography
                          fontFamily='geistMono'
                          fontSize='labelSm'
                          className='text-tokens-neutral-text-tertiary mt-1 select-text'
                        >
                          {email}
                        </Typography>
                      </div>
                      {(status === 'pending' || currentUser?.user_id !== id) && (
                        <ManageDropdown
                          member={member}
                          onAssignRole={handleAssignRole}
                          onRemoveUser={handleRemoveUser}
                          onCancelInvite={handleCancelInvite}
                        />
                      )}
                    </div>
                    <div className='mt-2 flex items-center justify-between gap-2'>
                      <div>{renderRoleCell(member)}</div>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        className='text-gray-900 truncate max-w-[150px] text-right select-none'
                      >
                        {getWorkspaceDisplay(member)}
                      </Typography>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <List
              columns={[
                { label: 'People', flex: 2, align: 'left', headerClassName: ' select-none' },
                {
                  label: 'Role',
                  flex: 1.5,
                  align: 'left',
                  headerClassName: ' select-none'
                },
                {
                  label: 'Workspaces',
                  flex: 2,
                  align: 'left',
                  headerClassName: ' select-none'
                },
                { label: 'Email', flex: 2, align: 'left', headerClassName: ' select-none' },
                {
                  label: 'Manage',
                  flex: 0.8,
                  align: 'right',
                  headerClassName: ' select-none'
                }
              ]}
              values={paginatedMembers.map(member => {
                const { id, name, email, status } = member
                const uniqueKey = id ?? email
                return [
                  <Typography
                    key={`name-${uniqueKey}`}
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='medium'
                    className={
                      status === 'pending'
                        ? 'text-gray-400 italic select-none'
                        : 'text-gray-900 select-text'
                    }
                  >
                    {name ?? 'Pending invitation'}
                  </Typography>,
                  <div key={`role-${uniqueKey}`}>{renderRoleCell(member)}</div>,
                  <CustomTooltip
                    key={`workspace-${uniqueKey}`}
                    content={(() => {
                      // For billing admins, show all non-personal workspaces in the account
                      // For regular members, show only their assigned workspaces
                      const workspacesToShow = member.billingAdmin
                        ? nonPersonalWorkspaces
                        : member.workspaces || []

                      if (workspacesToShow.length === 0) return null

                      return (
                        <div className='bg-white px-2 py-[6px] rounded-[6px]'>
                          <div className='mb-0 whitespace-pre-wrap'>
                            <Typography
                              fontFamily='geist'
                              fontSize='titleSm'
                              fontWeight='bold'
                              className='text-tokens-neutral-text-primary'
                              style={{ display: 'inline' }}
                            >
                              {member.name}
                            </Typography>
                            <Typography
                              fontFamily='geist'
                              fontSize='bodySm'
                              fontWeight='medium'
                              className='text-tokens-neutral-text-primary'
                              style={{ display: 'inline' }}
                            >
                              {"'s workspaces"}
                            </Typography>
                          </div>
                          <div className='h-4' />
                          <ul className='list-disc m-0 ps-[1.5em]'>
                            {workspacesToShow.map(ws => (
                              <li key={ws.org_id} className='mb-0'>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='bodySm'
                                  fontWeight='medium'
                                  className='text-tokens-neutral-text-secondary'
                                  style={{ display: 'inline' }}
                                >
                                  {ws.org_name}{' '}
                                </Typography>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='bodySm'
                                  fontWeight='medium'
                                  className='text-tokens-neutral-text-quaternary'
                                  style={{ display: 'inline' }}
                                >
                                  -{' '}
                                  {member.billingAdmin
                                    ? 'Account Admin'
                                    : ws.role === 'admin'
                                      ? 'Workspace Admin'
                                      : 'Member'}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    })()}
                    placement='right'
                  >
                    <div className='cursor-pointer select-none'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        className='text-gray-900 truncate max-w-[150px] block'
                      >
                        {getWorkspaceDisplay(member)}
                      </Typography>
                    </div>
                  </CustomTooltip>,
                  <Typography
                    key={`email-${uniqueKey}`}
                    fontFamily='geist'
                    fontSize='bodySm'
                    className='text-[rgba(0,0,0,0.88)] select-text'
                  >
                    {email}
                  </Typography>,
                  <div key={`manage-${uniqueKey}`} className='flex justify-end select-none'>
                    {(status === 'pending' || currentUser?.user_id !== id) && (
                      <ManageDropdown
                        member={member}
                        onAssignRole={handleAssignRole}
                        onRemoveUser={handleRemoveUser}
                        onCancelInvite={handleCancelInvite}
                      />
                    )}
                  </div>
                ]
              })}
              headerClassName='bg-transparent'
              bodyClassName='bg-transparent'
              rowClassName='rounded-[8px] transition-colors duration-150 select-none'
              rowHeight='48px'
            />
          )}
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            className='mt-1'
          />
        )}
      </div>

      {/* Role Assignment Confirmation Modal */}
      <Modal isOpen={roleConfirmModal.isOpen} onClose={closeRoleConfirmModal}>
        <div className='p-8'>
          <Typography fontFamily='geist' fontSize='titleLg' fontWeight='bold' className='mb-6'>
            {roleConfirmModal.role === 'admin' ? 'Set User as Account Admin' : 'Set User as Member'}
          </Typography>

          <Typography fontFamily='geist' fontSize='bodyMd' className='text-[rgba(0,0,0,0.88)] mb-4'>
            This action will assign the user as{' '}
            {roleConfirmModal.role === 'admin' ? 'an Account Admin' : 'a Member'} and trigger a
            confirmation email notifying them of their new role within your account.
          </Typography>

          <Typography fontFamily='geist' fontSize='bodyMd' className='text-[rgba(0,0,0,0.88)] mb-8'>
            Do you want to proceed?
          </Typography>

          <div className='flex justify-end gap-3'>
            <Button
              variant='outlined'
              onClick={closeRoleConfirmModal}
              className='px-6 py-2 rounded-xl border border-[#D6D6D6] bg-white text-[rgba(0,0,0,0.88)]'
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={confirmAssignRole}
              className='px-6 py-2 rounded-xl bg-[#1F1F1F] text-white'
            >
              Yes, proceed
            </Button>
          </div>
        </div>
      </Modal>

      {/* Remove User Confirmation Modal */}
      <Modal isOpen={removeConfirmModal.isOpen} onClose={closeRemoveConfirmModal}>
        <div className='p-8'>
          <Typography fontFamily='geist' fontSize='titleLg' fontWeight='bold' className='mb-6'>
            Remove User from {removeConfirmModal.member?.billingAdmin ? 'Account' : 'Workspace'}
          </Typography>

          <Typography fontFamily='geist' fontSize='bodyMd' className='text-[rgba(0,0,0,0.88)] mb-4'>
            {removeConfirmModal.member?.billingAdmin ? (
              <>
                <strong>
                  {removeConfirmModal.member?.name || removeConfirmModal.member?.email}
                </strong>{' '}
                is an <strong>Account Admin</strong>. Removing them will:
                <ul className='list-disc ml-6 mt-2'>
                  <li>Remove their Account Admin privileges</li>
                  <li>Remove them from the current workspace</li>
                </ul>
              </>
            ) : (
              <>
                This will remove{' '}
                <strong>
                  {removeConfirmModal.member?.name || removeConfirmModal.member?.email}
                </strong>{' '}
                from the current workspace. They will no longer have access to this workspace.
              </>
            )}
          </Typography>

          <Typography fontFamily='geist' fontSize='bodyMd' className='text-[rgba(0,0,0,0.88)] mb-8'>
            Do you want to proceed?
          </Typography>

          <div className='flex justify-end gap-3'>
            <Button
              variant='outlined'
              onClick={closeRemoveConfirmModal}
              className='px-6 py-2 rounded-xl border border-[#D6D6D6] bg-white text-[rgba(0,0,0,0.88)]'
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={confirmRemoveUser}
              className='px-6 py-2 rounded-xl'
            >
              Yes, remove user
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function AccountComponent() {
  const readOnly = useManageAccountReadOnly()
  const isCompact = useCompactView()
  const { billingInfo, updateBilling } = useBillingContext()
  const [organizationName, setOrganizationName] = useState<string>(billingInfo?.name || '')
  const [adminFullName, setAdminFullName] = useState<string>(billingInfo?.billing_to || '')
  const [adminWorkEmail, setAdminWorkEmail] = useState<string>(billingInfo?.billing_email || '')

  const currentValuesRef = useRef({
    organizationName: billingInfo?.name || '',
    adminFullName: billingInfo?.billing_to || '',
    adminWorkEmail: billingInfo?.billing_email || '',
    billingInfo,
    updateBilling
  })

  currentValuesRef.current = {
    organizationName,
    adminFullName,
    adminWorkEmail,
    billingInfo,
    updateBilling
  }

  useEffect(() => {
    return () => {
      const {
        organizationName: currentOrgName,
        adminFullName: currentAdminName,
        adminWorkEmail: currentEmail,
        billingInfo: currentBilling,
        updateBilling: currentUpdate
      } = currentValuesRef.current

      if (readOnly) return

      console.log('Component unmounting - saving changes')

      // Only update if we have values and they've changed from the original billing info
      if (
        currentBilling &&
        (currentOrgName !== currentBilling.name ||
          currentAdminName !== currentBilling.billing_to ||
          currentEmail !== currentBilling.billing_email)
      ) {
        currentUpdate({
          name: currentOrgName,
          billing_to: currentAdminName,
          billing_email: currentEmail
        })
      }
    }
  }, [readOnly])

  return (
    <div className={`flex flex-col ${isCompact ? 'gap-8' : 'gap-12'}`}>
      <div className='flex flex-col gap-2'>
        <Typography
          fontFamily='geistMono'
          fontSize='labelMd'
          fontWeight='semibold'
          className='text-gray-600 select-none'
        >
          Organization Name
        </Typography>
        <InputArea
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const v = e.target.value
            setOrganizationName(v)
          }}
          value={organizationName}
          isDisabled={readOnly}
          className={isCompact ? 'w-full h-[48px]' : 'w-[360px] h-[48px]'}
          aria-label='Organization name input'
        />
      </div>

      <div className={isCompact ? 'flex flex-col gap-4' : 'flex flex-row gap-4'}>
        <div className='flex flex-col gap-2'>
          <Typography
            fontFamily='geistMono'
            fontSize='labelMd'
            fontWeight='semibold'
            className='text-gray-600 select-none'
          >
            Admin Full Name
          </Typography>
          <InputArea
            placeholder='Admin Full Name'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const v = e.target.value
              setAdminFullName(v)
            }}
            value={adminFullName}
            isDisabled={readOnly}
            className={isCompact ? 'w-full h-[48px]' : 'w-[246px] h-[48px]'}
            aria-label='Admin full name input'
          />
        </div>
        <div className='flex flex-col gap-2'>
          <Typography
            fontFamily='geistMono'
            fontSize='labelMd'
            fontWeight='semibold'
            className='text-gray-600 select-none'
          >
            Admin Work Email
          </Typography>
          <InputArea
            placeholder='admin@company.com'
            isDisabled={true}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const v = e.target.value
              setAdminWorkEmail(v)
            }}
            value={adminWorkEmail}
            className={isCompact ? 'w-full h-[48px]' : 'w-[246px] h-[48px]'}
          />
        </div>
      </div>
    </div>
  )
}

function LicenseComponent({ licenseTabAction }: { licenseTabAction?: React.ReactNode }) {
  const readOnly = useManageAccountReadOnly()
  const isCompact = useCompactView()
  const {
    billingInfo,
    allLicenses: licensesData,
    allLicensesLoading,
    refetchAllLicenses
  } = useBillingContext()
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [optimisticLicenses, setOptimisticLicenses] = useState<LicenseResponse[] | null>(null)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const [isReverting, setIsReverting] = useState(false)
  const [selectedLicenseForCancel, setSelectedLicenseForCancel] = useState<LicenseResponse | null>(
    null
  )

  const licenses = optimisticLicenses || licensesData?.licenses || null
  const isLoading = allLicensesLoading && !hasLoadedOnce

  // Mark as loaded once we have data
  useEffect(() => {
    if (licensesData?.licenses && !hasLoadedOnce) {
      setHasLoadedOnce(true)
    }
  }, [licensesData, hasLoadedOnce])

  // Sync optimistic state with actual data
  useEffect(() => {
    if (licensesData?.licenses) {
      setOptimisticLicenses(licensesData.licenses)
    }
  }, [licensesData])

  // Check if any license has subscription (for showing cancel/revert actions)
  const hasSubscriptionLicense = licenses?.some(license => !license.is_free_license)

  const handleOpenCancelDialog = (license?: LicenseResponse) => {
    if (readOnly) return
    setSelectedLicenseForCancel(license || null)
    setCancelOpen(true)
  }

  const handleCloseCancelDialog = () => {
    setCancelOpen(false)
    setSelectedLicenseForCancel(null)
  }

  const handleConfirmCancel = async () => {
    if (readOnly) return
    if (!billingInfo?.id || !selectedLicenseForCancel) {
      BetterToast.error('Billing information or license not available')
      return
    }
    setIsCanceling(true)
    try {
      const result = await cancelSubscriptionService(
        billingInfo.id,
        selectedLicenseForCancel.instance_id
      )
      if (result.success) {
        BetterToast.success(result.message || 'Subscription cancellation scheduled')
        setCancelOpen(false)
        setSelectedLicenseForCancel(null)

        // Optimistic update - mark the license as pending cancellation
        if (optimisticLicenses && selectedLicenseForCancel) {
          setOptimisticLicenses(
            optimisticLicenses.map(license =>
              license.instance_id === selectedLicenseForCancel.instance_id
                ? { ...license, auto_renew: false }
                : license
            )
          )
        }

        // Refresh licenses to reflect the updated status from server
        await refetchAllLicenses()
      }
    } catch {
      // Error is already shown by the API layer, just close the dialog
      setCancelOpen(false)
      setSelectedLicenseForCancel(null)
    } finally {
      setIsCanceling(false)
    }
  }

  const handleRevertCancel = async (license: LicenseResponse) => {
    if (readOnly) return
    if (!billingInfo?.id) {
      BetterToast.error('Billing information not available')
      return
    }
    setIsReverting(true)
    try {
      const result = await revertCancellationService(billingInfo.id, license.instance_id)
      if (result.success) {
        BetterToast.success(result.message || 'Cancellation reverted successfully')

        // Optimistic update - mark the license as active again
        if (optimisticLicenses) {
          setOptimisticLicenses(
            optimisticLicenses.map(lic =>
              lic.instance_id === license.instance_id ? { ...lic, auto_renew: true } : lic
            )
          )
        }

        // Refresh licenses to reflect the updated status from server
        await refetchAllLicenses()
      }
    } catch {
      // Error is already shown by the API layer
    } finally {
      setIsReverting(false)
    }
  }

  const isPendingCancellation = (license: LicenseResponse) => {
    return (
      (!license.is_free_license && !license.auto_renew) ||
      license.status?.toLowerCase().includes('cancel')
    )
  }

  const renderLicenseAction = (license: LicenseResponse) => {
    if (license.is_free_license) {
      return (
        <Typography fontFamily='geist' fontSize='bodySm' className='text-gray-400'>
          -
        </Typography>
      )
    }

    if (readOnly) {
      return (
        <Typography
          fontFamily='geist'
          fontSize='bodySm'
          className='text-tokens-neutral-text-tertiary'
        >
          —
        </Typography>
      )
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='borderless'
            className='w-8 h-8 p-0 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center'
            isDisabled={isReverting}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <circle cx='8' cy='4' r='1.5' fill='rgba(0,0,0,0.45)' />
              <circle cx='8' cy='8' r='1.5' fill='rgba(0,0,0,0.45)' />
              <circle cx='8' cy='12' r='1.5' fill='rgba(0,0,0,0.45)' />
            </svg>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-[180px]'>
          {isPendingCancellation(license) ? (
            <DropdownMenuItem
              className='text-[14px] font-medium text-[#16A34A] rounded-lg px-3 py-2'
              onClick={() => handleRevertCancel(license)}
              disabled={isReverting}
            >
              {isReverting ? 'Reverting...' : 'Undo Cancel'}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              className='text-[14px] font-medium text-[#C22C15] rounded-lg px-3 py-2'
              onClick={() => handleOpenCancelDialog(license)}
            >
              Request Cancel
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-32'>
        <Typography fontFamily='geist' fontSize='bodyMd' className='text-gray-500 ml-3'>
          Loading licenses...
        </Typography>
      </div>
    )
  }

  if (!licenses || licenses.length === 0) {
    return (
      <div className='flex items-center justify-center h-32'>
        <Typography fontFamily='geist' fontSize='bodyMd' className='text-gray-500'>
          No active licenses found
        </Typography>
      </div>
    )
  }

  return (
    <div className='flex flex-col items-start gap-6 self-stretch'>
      {licenseTabAction && <div className='w-full'>{licenseTabAction}</div>}

      {/* License List */}
      <div className='flex flex-col items-start gap-6 self-stretch px-[16px] py-7 rounded-[24px] bg-white/50 border border-white'>
        {isCompact ? (
          <div className='w-full flex flex-col gap-3'>
            {licenses.map(license => (
              <div
                key={`license-card-${license.instance_id}`}
                className='w-full rounded-2xl border border-white bg-white/60 p-4'
              >
                <div className='flex items-start justify-between gap-2 mb-3'>
                  <div className='flex flex-col gap-1'>
                    <Tag variant={license.license_name === 'enterprise' ? 'info' : 'link'}>
                      {license.license_label}
                    </Tag>
                  </div>
                  {hasSubscriptionLicense && (
                    <div className='shrink-0'>{renderLicenseAction(license)}</div>
                  )}
                </div>
                <div className='grid grid-cols-2 gap-2'>
                  {isBillableFlowLicense(license) ? (
                    <div>
                      <Typography
                        fontFamily='geistMono'
                        fontSize='labelSm'
                        className='text-tokens-neutral-text-tertiary'
                      >
                        Billable Flows
                      </Typography>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='text-tokens-neutral-text-primary'
                      >
                        {formatBillableFlowsLimit(license)}
                      </Typography>
                    </div>
                  ) : (
                    <div>
                      <Typography
                        fontFamily='geistMono'
                        fontSize='labelSm'
                        className='text-tokens-neutral-text-tertiary'
                      >
                        Credit
                      </Typography>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='text-tokens-neutral-text-primary'
                      >
                        {formatCreditLimit(license)}
                      </Typography>
                    </div>
                  )}
                  <div>
                    <Typography
                      fontFamily='geistMono'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-tertiary'
                    >
                      Start
                    </Typography>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      fontWeight='medium'
                      className='text-tokens-neutral-text-primary'
                    >
                      {formatLicenseDate(license.start_date)}
                    </Typography>
                  </div>
                  <div>
                    <Typography
                      fontFamily='geistMono'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-tertiary'
                    >
                      {getLicensePeriodEndLabel(license)}
                    </Typography>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      fontWeight='medium'
                      className='text-tokens-neutral-text-primary'
                    >
                      {formatLicenseDate(license.end_date)}
                    </Typography>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <List
            columns={[
              { label: 'Plan', width: '220px', flex: 2, align: 'left' as const },
              { label: 'Credit Limit', width: '96px', flex: 1, align: 'center' as const },
              { label: 'Billable Flows', width: '96px', flex: 1, align: 'center' as const },
              { label: 'Start Date', width: '96px', flex: 1, align: 'center' as const },
              { label: 'Renewal / End', width: '136px', flex: 1, align: 'center' as const },
              ...(hasSubscriptionLicense
                ? [{ label: '', width: '48px', flex: 0, align: 'center' as const }]
                : [])
            ]}
            values={licenses.map(license => [
              <TruncateCell
                key={`license-${license.instance_id}`}
                tooltipContent={license.license_label}
              >
                <div className='flex flex-col items-start gap-1 min-w-0'>
                  <Tag variant={license.license_name === 'enterprise' ? 'info' : 'link'}>
                    {license.license_label}
                  </Tag>
                </div>
              </TruncateCell>,
              <TruncateCell
                key={`credits-${license.instance_id}`}
                tooltipContent={formatCreditLimit(license)}
              >
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-tokens-neutral-text-primary'
                >
                  {formatCreditLimit(license)}
                </Typography>
              </TruncateCell>,
              <TruncateCell
                key={`billable-flows-${license.instance_id}`}
                tooltipContent={formatBillableFlowsLimit(license)}
              >
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-tokens-neutral-text-primary'
                >
                  {formatBillableFlowsLimit(license)}
                </Typography>
              </TruncateCell>,
              <TruncateCell
                key={`start-${license.instance_id}`}
                tooltipContent={formatLicenseDate(license.start_date)}
              >
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-tokens-neutral-text-primary'
                >
                  {formatLicenseDate(license.start_date)}
                </Typography>
              </TruncateCell>,
              <TruncateCell
                key={`end-${license.instance_id}`}
                tooltipContent={`${getLicensePeriodEndLabel(license)} ${formatLicenseDate(license.end_date)}`}
              >
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-tokens-neutral-text-primary'
                >
                  {formatLicenseDate(license.end_date)}
                </Typography>
              </TruncateCell>,
              ...(hasSubscriptionLicense
                ? [
                    <div key={`actions-${license.instance_id}`} className='flex justify-center'>
                      {renderLicenseAction(license)}
                    </div>
                  ]
                : [])
            ])}
          />
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal isOpen={cancelOpen} onClose={handleCloseCancelDialog}>
        <div className='flex flex-col gap-4 px-6 py-8'>
          <div className='flex items-center gap-3'>
            <svg
              className='h-6 w-6 text-red-600'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
            <Typography
              fontFamily='geist'
              fontSize='titleLg'
              fontWeight='medium'
              className='text-gray-900'
            >
              Cancel Subscription
              {selectedLicenseForCancel && ` - ${selectedLicenseForCancel.license_label}`}
            </Typography>
          </div>
          <div className='flex flex-col justify-end items-start w-full gap-6'>
            <Typography fontFamily='geist' fontSize='bodySm' className='text-gray-600'>
              Are you sure you want to cancel your{' '}
              {selectedLicenseForCancel?.license_label || 'subscription'}?
            </Typography>

            <div className='w-full p-4 bg-blue-50 rounded-xl border border-blue-200'>
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-2'>
                  <svg
                    className='h-5 w-5 text-blue-600'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                  <Typography
                    fontFamily='geist'
                    fontSize='titleSm'
                    fontWeight='semibold'
                    className='text-blue-800'
                  >
                    Keep Access Until Period Ends
                  </Typography>
                </div>
                <Typography fontFamily='geist' fontSize='bodySm' className='text-blue-700'>
                  Your subscription will be canceled at the end of your current billing period. You
                  will retain full access to all features until then.
                </Typography>
              </div>
            </div>

            <div className='flex gap-3 justify-end w-full h-10'>
              <Button
                variant='outlined'
                className='bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors w-full border border-gray-300'
                onClick={handleCloseCancelDialog}
                disabled={isCanceling}
              >
                <Typography fontFamily='geist' fontSize='bodyMd' className='text-inherit'>
                  Keep Subscription
                </Typography>
              </Button>
              <Button
                variant='primary'
                className='bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 w-full'
                onClick={handleConfirmCancel}
                disabled={isCanceling}
              >
                <Typography fontFamily='geist' fontSize='bodyMd' className='text-inherit'>
                  {isCanceling ? 'Canceling...' : 'Cancel Subscription'}
                </Typography>
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

const CARD_BRAND_DISPLAY: Record<string, { label: string; color: string }> = {
  visa: { label: 'Visa', color: '#1A1F71' },
  mastercard: { label: 'Mastercard', color: '#EB001B' },
  amex: { label: 'Amex', color: '#006FCF' },
  discover: { label: 'Discover', color: '#FF6000' }
}

function CardBrandBadge({ brand }: { brand: string }) {
  const normalized = brand.toLowerCase()
  const info = CARD_BRAND_DISPLAY[normalized]
  const displayName = info?.label ?? brand.charAt(0).toUpperCase() + brand.slice(1)
  const color = info?.color ?? '#555'
  return (
    <div
      className='inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-gray-200 bg-white'
      style={{ minWidth: 0 }}
    >
      <span className='text-[10px] font-bold leading-none select-none' style={{ color }}>
        {displayName.toUpperCase()}
      </span>
    </div>
  )
}

function BillingComponent() {
  const readOnly = useManageAccountReadOnly()
  const isCompact = useCompactView()
  const { billingInfo, invoices, allLicenses } = useBillingContext()
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null)
  const [creditPurchases, setCreditPurchases] = useState<BillingHistoryItem[]>([])
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false)

  const handleChangePaymentMethod = async () => {
    if (readOnly || !billingInfo?.id) return
    setIsOpeningPortal(true)
    try {
      const response = await createCustomerPortalSession(billingInfo.id)
      const url = response.redirect_url ?? response.url
      if (url) {
        window.open(url, '_blank')
      } else {
        BetterToast.error('Failed to open billing portal')
      }
    } catch {
      BetterToast.error('Failed to open billing portal')
    } finally {
      setIsOpeningPortal(false)
    }
  }

  useEffect(() => {
    if (!billingInfo?.id) return

    getPaymentDetails(billingInfo.id)
      .then(details => {
        setHasPaymentMethod(details.has_payment_method)
        if (details.has_payment_method && details.payment_method_last4) {
          setPaymentMethod({
            brand: details.payment_method_brand || 'Card',
            last4: details.payment_method_last4
          })
        }
      })
      .catch(() => {
        const metadata = billingInfo.billing_metadata
        if (metadata?.payment_method_setup && metadata.payment_method_last4) {
          setHasPaymentMethod(true)
          setPaymentMethod({
            brand: metadata.payment_method_brand || 'Card',
            last4: metadata.payment_method_last4
          })
        }
      })
  }, [billingInfo?.id, billingInfo?.billing_metadata])

  /* c8 ignore start -- c8 cannot reliably track coverage inside React component bodies (JSX source map issue). Logic is tested in manageAccountHelpers.test.ts and rendering in ManageAccountDialog.fixtures.test.tsx */
  // Fetch credit purchase history to merge into billing history
  useEffect(() => {
    if (!billingInfo?.id) return

    getCreditBalance(billingInfo.id)
      .then(balance => {
        const purchases: BillingHistoryItem[] = balance.purchase_history
          .filter(p => !p.refunded)
          .map(purchase => ({
            date: purchase.purchased_at ? new Date(purchase.purchased_at) : new Date(),
            amount: (purchase.amount_total_cents ?? 0) / 100,
            status: 'paid',
            paid: true,
            pdfUrl: purchase.receipt_url,
            type: 'credit_purchase' as const,
            description: `${purchase.credits} credits`
          }))
        setCreditPurchases(purchases)
      })
      .catch(err => {
        console.warn('Failed to fetch credit purchases for billing history:', err)
      })
  }, [billingInfo?.id])

  // Merge invoices and credit purchases into unified billing history, sorted by date descending
  const billingHistory = useMemo(
    () => buildBillingHistory(invoices, creditPurchases),
    [invoices, creditPurchases]
  )

  const isBillableFlowPlan = getIsBillableFlowPlan(null, allLicenses)
  const hasStripeSubscription = allLicenses?.licenses?.some(l => !!l.subscription_id) ?? false

  /* c8 ignore stop */

  return (
    <div className={`flex flex-col items-start self-stretch ${isCompact ? 'gap-6' : 'gap-12'}`}>
      {/* Credit Balance Section - shown for quota-based (credit) plans including Flex */}
      {!isBillableFlowPlan && <CreditPurchaseCard variant='balance' readOnly={readOnly} />}

      {hasStripeSubscription && (
        <div className='flex flex-col items-start gap-4 self-stretch'>
          <div className='flex items-center justify-between self-stretch'>
            <Typography
              fontFamily='geist'
              fontSize='titleSm'
              fontWeight='semibold'
              className='text-[rgba(0,0,0,0.88)] select-none'
            >
              Payment Method
            </Typography>
            <Button
              variant='borderless'
              onClick={handleChangePaymentMethod}
              disabled={readOnly || isOpeningPortal}
              className='text-blue-600 hover:text-blue-800 px-3 py-1 h-auto'
            >
              <Typography fontFamily='geist' fontSize='bodySm' className='text-inherit'>
                {isOpeningPortal
                  ? 'Opening...'
                  : hasPaymentMethod
                    ? 'Change'
                    : 'Add Payment Method'}
              </Typography>
            </Button>
          </div>
          {hasPaymentMethod && paymentMethod ? (
            <div className='flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white/60 self-stretch'>
              <CardBrandBadge brand={paymentMethod.brand} />
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='medium'
                className='text-gray-700 select-none'
              >
                ending in {paymentMethod.last4}
              </Typography>
            </div>
          ) : (
            <div className='flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-gray-300 bg-gray-50/50 self-stretch'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-gray-400 select-none'
              >
                No payment method on file
              </Typography>
            </div>
          )}
        </div>
      )}
      <div className='flex flex-col items-start gap-6 self-stretch'>
        <Typography
          fontFamily='geist'
          fontSize='titleSm'
          fontWeight='semibold'
          className='text-[rgba(0,0,0,0.88)] select-none'
        >
          Billing History
        </Typography>
        {billingHistory.length > 0 ? (
          isCompact ? (
            <div className='w-full flex flex-col gap-3'>
              {billingHistory.map((item, index) => (
                <div
                  key={`billing-card-${index}`}
                  className='w-full rounded-2xl border border-white bg-white/60 p-4'
                >
                  <div className='flex items-start justify-between gap-3'>
                    <div className='min-w-0'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='semibold'
                        className='text-tokens-neutral-text-primary select-none'
                      >
                        {item.type === 'credit_purchase'
                          ? `Credit Purchase (${item.description})`
                          : 'Subscription'}
                      </Typography>
                      <Typography
                        fontFamily='geistMono'
                        fontSize='labelSm'
                        className='text-tokens-neutral-text-tertiary mt-1 select-none'
                      >
                        {item.date.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </Typography>
                    </div>
                    <div className='shrink-0 text-right'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='bold'
                        className='text-tokens-neutral-text-primary select-none'
                      >
                        ${item.amount.toFixed(0)}
                      </Typography>
                      <Typography
                        fontFamily='geistMono'
                        fontSize='labelSm'
                        className='text-tokens-neutral-text-tertiary select-none'
                      >
                        {item.paid ? 'paid' : item.status}
                      </Typography>
                    </div>
                  </div>
                  <div className='mt-3 flex justify-end'>
                    {item.pdfUrl ? (
                      <Button
                        variant='outlined'
                        onClick={() => {
                          if (!readOnly && item.pdfUrl) window.open(item.pdfUrl, '_blank')
                        }}
                        isDisabled={readOnly}
                        className='h-8 px-3 text-[12px]'
                      >
                        Invoice
                      </Button>
                    ) : (
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='text-gray-400 select-none'
                      >
                        N/A
                      </Typography>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <List
              columns={[
                { label: 'Date', flex: 1, align: 'left' },
                { label: 'Description', flex: 1.5, align: 'left' },
                { label: 'Amount', flex: 1, align: 'right' },
                { label: 'Status', flex: 1, align: 'left' },
                { label: 'Invoices', flex: 1, align: 'right' }
              ]}
              values={billingHistory.map((item, index) => [
                <Typography
                  key={`date-${index}`}
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-gray-700 select-none'
                  fontWeight='medium'
                >
                  {item.date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </Typography>,
                <Typography
                  key={`desc-${index}`}
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-gray-700 select-none'
                  fontWeight='medium'
                >
                  {item.type === 'credit_purchase'
                    ? `Credit Purchase (${item.description})`
                    : 'Subscription'}
                </Typography>,
                <Typography
                  key={`amount-${index}`}
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-gray-700 select-none'
                  fontWeight='bold'
                >
                  ${item.amount.toFixed(0)}
                </Typography>,
                <Typography
                  key={`status-${index}`}
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-gray-700 select-none'
                  fontWeight='bold'
                >
                  {item.paid ? 'paid' : item.status}
                </Typography>,
                item.pdfUrl ? (
                  <button
                    key={`invoice-${index}`}
                    type='button'
                    disabled={readOnly}
                    onClick={() => (readOnly ? undefined : window.open(item.pdfUrl, '_blank'))}
                    className='cursor-pointer hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed'
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                      fill='none'
                    >
                      <path
                        d='M9.87695 12.9102C9.89157 12.9288 9.91024 12.9439 9.93156 12.9543C9.95288 12.9647 9.97629 12.9701 10 12.9701C10.0237 12.9701 10.0471 12.9647 10.0684 12.9543C10.0898 12.9439 10.1084 12.9288 10.123 12.9102L12.3105 10.1426C12.3906 10.041 12.3184 9.89062 12.1875 9.89062H10.7402V3.28125C10.7402 3.19531 10.6699 3.125 10.584 3.125H9.41211C9.32617 3.125 9.25586 3.19531 9.25586 3.28125V9.88867H7.8125C7.68164 9.88867 7.60938 10.0391 7.68945 10.1406L9.87695 12.9102ZM17.1484 12.2266H15.9766C15.8906 12.2266 15.8203 12.2969 15.8203 12.3828V15.3906H4.17969V12.3828C4.17969 12.2969 4.10938 12.2266 4.02344 12.2266H2.85156C2.76562 12.2266 2.69531 12.2969 2.69531 12.3828V16.25C2.69531 16.5957 2.97461 16.875 3.32031 16.875H16.6797C17.0254 16.875 17.3047 16.5957 17.3047 16.25V12.3828C17.3047 12.2969 17.2344 12.2266 17.1484 12.2266Z'
                        fill='black'
                        fillOpacity='0.45'
                      />
                    </svg>
                  </button>
                ) : (
                  <Typography
                    key={`invoice-${index}`}
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='medium'
                    className='text-gray-400 select-none'
                  >
                    N/A
                  </Typography>
                )
              ])}
              bodyClassName='bg-transparent'
            />
          )
        ) : (
          <div className='py-8 text-center'>
            <Typography fontFamily='geist' fontSize='bodySm' className='text-gray-500 select-none'>
              No billing history available
            </Typography>
          </div>
        )}
      </div>
    </div>
  )
}

function UpgradeComponent({ onClose }: { onClose?: () => void }) {
  return <PlanComponent actionLabel='Go to Payment' onClose={onClose} />
}
export interface ManageAccountDialogProps {
  isOpen: boolean
  onClose: () => void
  initialTab?: Tab
  /** When true, all edits, purchases, invites, and billing actions are disabled (e.g. super admin). */
  readOnly?: boolean
  /** True only when a super admin opened this dialog against another org via
   *  the super-admin tool (`SuperAdminManageAccountDialog`). The dialog ORs
   *  this with `is_super_admin` from `useUserSpecificConfig` to decide whether
   *  to surface super-admin-only UI to the viewer. Does NOT mean "the current
   *  viewer is a super admin." */
  isSuperAdminImpersonation?: boolean
  /** Optional action surface rendered at the top of the Licenses tab. */
  licenseTabAction?: React.ReactNode
}

interface PaymentMethod {
  brand: string
  last4: string
}

// License tier hierarchy (higher = more priority)
// Source: migrations/versions/45_add_license_and_account_membership_tables.py
const PLAN_HIERARCHY: Record<string, number> = {
  flex: 0,
  pro: 1,
  pro_plus: 2,
  team: 3,
  teams: 3, // Label alias for team
  enterprise: 4,
  'enterprise-migrate': 4,
  'enterprise-migrate-assess': 4,
  'enterprise-migrate-accelerate': 4,
  'enterprise-migrate-refactor': 4
}

const ManageAccountDialog = ({
  isOpen,
  onClose,
  initialTab,
  readOnly = false,
  isSuperAdminImpersonation = false,
  licenseTabAction
}: ManageAccountDialogProps) => {
  const {
    billingInfo,
    allLicenses: licenseData,
    refetchAllLicenses,
    licenseTypes,
    isBillingAdmin
  } = useBillingContext()
  const { userConfig } = useUserSpecificConfig()
  const isSuperAdmin = userConfig?.is_super_admin ?? false
  const [activeTab, setActiveTab] = useState<Tab>(initialTab ?? 'usage')
  const [isLoading, setIsLoading] = useState(true)

  // Use license-based checks - memoize to prevent unnecessary recalculations
  const hasLicenses = (licenseData?.total_licenses ?? 0) > 0
  const hasEnterpriseLicense = useMemo(
    () =>
      licenseData?.licenses.some(license =>
        license.license_name?.toLowerCase().includes('enterprise')
      ) ?? false,
    [licenseData?.licenses]
  )
  const hasTeamLicense = useMemo(
    () =>
      licenseData?.licenses.some(license => license.license_name?.toLowerCase().includes('team')) ??
      false,
    [licenseData?.licenses]
  )
  // Check for unlimited credits using centralized utility
  const hasUnlimitedCredits = isUnlimited(licenseData?.credits_limit)

  // Determine current plan tier
  const currentPlanTier = useMemo(() => {
    if (!licenseData?.licenses.length) return 0 // flex (base tier)
    // Get the highest tier license
    return Math.max(
      ...licenseData.licenses.map(license => {
        const planName = license.license_name?.toLowerCase() || 'flex'
        return PLAN_HIERARCHY[planName] ?? 0
      })
    )
  }, [licenseData?.licenses])

  // Check if there are any upgradeable plans available
  const hasUpgradeablePlans = useMemo(() => {
    if (!licenseTypes || licenseTypes.length === 0) return false

    // Filter to plans that are higher tier than current and not enterprise (since enterprise requires sales contact)
    const upgradeablePlans = licenseTypes.filter(plan => {
      const planTier = PLAN_HIERARCHY[plan.name] ?? 0
      // Show upgrade if there's a higher tier available (excluding enterprise for self-service)
      return planTier > currentPlanTier && plan.name !== 'enterprise'
    })

    // Also show if team/lower plans want to contact sales for enterprise
    const hasEnterpriseOption =
      licenseTypes.some(plan => plan.name === 'enterprise') && currentPlanTier < 4

    return upgradeablePlans.length > 0 || hasEnterpriseOption
  }, [licenseTypes, currentPlanTier])

  // Determine if upgrade should be shown:
  // - Not for enterprise accounts (they can't self-upgrade)
  // - Only if there are actual plans available to upgrade to
  const canShowUpgrade = !hasEnterpriseLicense && !hasUnlimitedCredits && hasUpgradeablePlans

  // Reset to initialTab when dialog opens
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab)
    }
  }, [isOpen, initialTab])

  useEffect(() => {
    if (readOnly && activeTab === 'upgrade') {
      setActiveTab('usage')
    }
  }, [readOnly, activeTab])

  useEffect(() => {
    if (!isOpen) return
    setIsLoading(true)
    // Refetch license data when dialog opens to ensure fresh data
    if (billingInfo) {
      refetchAllLicenses().then(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [isOpen, billingInfo, refetchAllLicenses])

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab)
  }

  const getTabIcon = (tab: string) => {
    switch (tab) {
      case 'dashboard':
        return <DashboardTabIcon className='w-5 h-5' />
      case 'usage':
        return <PiPresentationChart className='w-5 h-5' />
      case 'users':
        return <AiOutlineTeam className='w-5 h-5' />
      case 'account':
        return <PiGear className='w-5 h-5' />
      case 'license':
        return <PiCertificate className='w-5 h-5' />
      case 'billing':
        return <PiReceipt className='w-5 h-5' />
      case 'upgrade':
        return <AiOutlineCrown className='w-5 h-5' />
      default:
        return null
    }
  }

  const TabButton = ({
    tab,
    label,
    icon,
    isBold = false,
    customColor
  }: {
    tab: Tab
    label: string
    icon?: React.ReactNode
    isBold?: boolean
    customColor?: string
  }) => (
    <Button
      variant='borderless'
      onClick={() => handleTabChange(tab)}
      className={`w-full h-[36px] justify-start px-3 text-center ${isBold ? 'font-bold' : 'font-medium'} text-[14px] leading-[22px] ${
        activeTab === tab
          ? 'bg-tokens-neutral-fill-fill-6 text-tokens-neutral-text-primary'
          : 'text-tokens-neutral-text-secondary hover:bg-tokens-neutral-bg-hover'
      }`}
      leftIcon={icon ?? getTabIcon(tab)}
      // Only force an inline color when an explicit customColor is supplied.
      // The previous default `var(--neutral-text-primary, rgba(0,0,0,0.88))`
      // referenced a CSS var that isn't defined in dark mode, so it fell back
      // to near-black and overrode the token classes above (black-on-black in
      // dark mode). Letting the className tokens win fixes both themes.
      style={customColor ? { color: customColor } : undefined}
    >
      {label}
    </Button>
  )

  // Map active tab to header title
  const tabTitleMap: Record<Tab, string> = {
    usage: isBillingAdmin ? 'Dashboard' : 'Usage',
    users: 'People',
    account: 'Account',
    license: 'Licenses',
    billing: 'Billing',
    upgrade: 'Upgrade'
  }
  const headerTitle = tabTitleMap[activeTab] ?? 'Dashboard'

  const isCompact = useCompactView()
  const pluginNavTabs: Array<{
    tab: Tab
    label: string
    icon?: React.ReactNode
    isBold?: boolean
    customColor?: string
  }> = [
    {
      tab: 'usage',
      label: isBillingAdmin ? 'Dashboard' : 'Usage',
      icon: isBillingAdmin ? getTabIcon('dashboard') : getTabIcon('usage')
    },
    ...(hasTeamLicense || hasEnterpriseLicense
      ? [{ tab: 'users' as Tab, label: 'People', icon: getTabIcon('users') }]
      : []),
    { tab: 'account', label: 'Account' },
    ...(hasLicenses ? [{ tab: 'license' as Tab, label: 'Licenses' }] : []),
    ...(isBillingAdmin && hasLicenses ? [{ tab: 'billing' as Tab, label: 'Billing' }] : []),
    ...(isBillingAdmin && canShowUpgrade && !readOnly
      ? [
          {
            tab: 'upgrade' as Tab,
            label: 'Upgrade',
            isBold: true,
            customColor: 'var(--status-link-1, #846FE1)'
          }
        ]
      : [])
  ]

  return (
    <ManageAccountReadOnlyProvider value={readOnly}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className={
          isCompact
            ? 'w-full max-w-[calc(100vw-24px)] h-[calc(100vh-32px)]'
            : 'w-full max-w-[1048px] h-[calc(100vh-120px)]'
        }
        overlayClassName={isCompact ? 'px-3' : 'px-[60px]'}
        hideCloseButton
        disableOutsideClick
        rawContent
      >
        <div className={`flex flex-1 min-h-0 ${isCompact ? 'flex-col' : 'flex-row'}`}>
          {/* Nav sidebar - spans full modal height */}
          {!isCompact && (
            <div className='bg-white border-r border-[#EAEAEA] flex w-[180px] min-w-[180px] py-[24px] px-[12px] flex-col items-start gap-[48px] self-stretch'>
              <div className='flex flex-col justify-center items-start w-full pl-[16px]'>
                <Typography
                  fontFamily='geist'
                  fontSize='titleSm'
                  fontWeight='bold'
                  className='text-black select-none'
                >
                  Manage Account
                </Typography>
              </div>
              <nav className='w-full flex flex-col gap-1'>
                <TabButton
                  tab='usage'
                  label={isBillingAdmin ? 'Dashboard' : 'Usage'}
                  icon={isBillingAdmin ? getTabIcon('dashboard') : getTabIcon('usage')}
                />
                {/* Show Users tab ONLY for Team and Enterprise plans (multi-user plans) */}
                {(hasTeamLicense || hasEnterpriseLicense) && (
                  <TabButton tab='users' label='People' icon={getTabIcon('users')} />
                )}
                <TabButton tab='account' label='Account' />
                {/* Show Licenses tab for all paid accounts */}
                {hasLicenses && <TabButton tab='license' label='Licenses' />}
                <div className='w-full h-px bg-tokens-neutral-border-2 my-4' />
                {/* Show Billing and Upgrade tabs only for billing account admins */}
                {isBillingAdmin && (
                  <>
                    {hasLicenses && <TabButton tab='billing' label='Billing' />}
                    {/* Show Upgrade for non-enterprise accounts (including Team who can contact sales) */}
                    {canShowUpgrade && !readOnly && (
                      <TabButton
                        tab='upgrade'
                        label='Upgrade'
                        isBold
                        customColor='var(--status-link-1, #846FE1)'
                      />
                    )}
                  </>
                )}
              </nav>
            </div>
          )}

          {/* Right side: header + content */}
          <div className='flex-1 min-w-0 flex flex-col min-h-0'>
            {/* Header aligned with content area only */}
            <DialogHeader title={headerTitle} onClose={onClose} className='shrink-0 select-none' />
            {readOnly && (
              <div className='shrink-0 px-6 py-2.5 bg-tokens-neutral-fill-fill-6 border-b border-tokens-neutral-border-2'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-tokens-neutral-text-secondary'
                >
                  View only — you can browse this account but cannot make changes.
                </Typography>
              </div>
            )}
            {isCompact && (
              <div className='px-3 py-2 border-b border-tokens-neutral-border-2 bg-white'>
                <div className='flex items-center gap-2 overflow-x-auto no-scrollbar'>
                  {pluginNavTabs.map(({ tab, label, icon, isBold, customColor }) => (
                    <Button
                      key={tab}
                      variant='borderless'
                      onClick={() => handleTabChange(tab)}
                      leftIcon={icon}
                      className={`h-9 px-3 rounded-xl whitespace-nowrap ${
                        isBold ? 'font-bold' : 'font-medium'
                      } text-[13px] leading-[20px] ${
                        activeTab === tab
                          ? 'bg-tokens-neutral-fill-fill-6 text-tokens-neutral-text-primary'
                          : 'text-tokens-neutral-text-secondary'
                      }`}
                      style={{
                        color: customColor ?? undefined
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Content area */}
            {activeTab === 'upgrade' ? (
              isLoading || !licenseData ? (
                <div className='flex-1 flex items-center justify-center bg-[#EFF0F3]'>
                  <Typography fontFamily='geist' fontSize='bodyMd' className='text-gray-500 ml-3'>
                    Loading usage data...
                  </Typography>
                </div>
              ) : (
                <UpgradeComponent onClose={onClose} />
              )
            ) : (
              <div className='flex-1 min-w-0 min-h-0 overflow-y-auto bg-[#EFF0F3]'>
                {isLoading || !licenseData ? (
                  <div className='flex items-center justify-center h-32'>
                    <Typography fontFamily='geist' fontSize='bodyMd' className='text-gray-500 ml-3'>
                      Loading usage data...
                    </Typography>
                  </div>
                ) : (
                  <>
                    {activeTab === 'usage' &&
                      (isBillingAdmin ? (
                        <div className={isCompact ? 'py-4 px-3' : 'py-6 px-[48px]'}>
                          <DashboardContent
                            readOnly={readOnly}
                            isSuperAdminView={isSuperAdmin || isSuperAdminImpersonation}
                          />
                        </div>
                      ) : (
                        <div className={isCompact ? 'py-4 px-3 w-full' : 'py-6 px-[48px] w-full'}>
                          <UsageComponent
                            licenseData={licenseData}
                            handleTabChange={handleTabChange}
                            onClose={onClose}
                            canShowUpgrade={!readOnly && canShowUpgrade}
                          />
                        </div>
                      ))}
                    {activeTab === 'users' && (
                      <div className={isCompact ? 'py-4 px-3' : 'py-6 px-[48px]'}>
                        <UsersComponent />
                      </div>
                    )}
                    {activeTab === 'account' && (
                      <div className={isCompact ? 'py-4 px-3' : 'py-6 px-[48px]'}>
                        <AccountComponent />
                      </div>
                    )}
                    {activeTab === 'license' && (
                      <div className={isCompact ? 'py-4 px-3' : 'py-6 px-[48px]'}>
                        <LicenseComponent licenseTabAction={licenseTabAction} />
                      </div>
                    )}
                    {activeTab === 'billing' && (
                      <div className={isCompact ? 'py-4 px-3' : 'py-6 px-[48px]'}>
                        <BillingComponent />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </ManageAccountReadOnlyProvider>
  )
}

export default ManageAccountDialog
